import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Plus } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

const TABS = [
  { key: "colleges", label: "New colleges", hasState: true },
  { key: "courses", label: "New courses" },
  { key: "categories", label: "New categories" },
  { key: "quotas", label: "New quotas" },
];

export default function AdminReview() {
  const { batchId } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("colleges");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addDrafts, setAddDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      const { data: resp } = await api.get(`/admin/uploads/${batchId}/review`);
      setData(resp);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    if (!data) return { colleges: 0, courses: 0, categories: 0, quotas: 0, total: 0 };
    const d = data.unresolved || {};
    const total = Object.values(d).reduce((s, arr) => s + arr.length, 0);
    return {
      colleges: (d.colleges || []).length,
      courses: (d.courses || []).length,
      categories: (d.categories || []).length,
      quotas: (d.quotas || []).length,
      total,
    };
  }, [data]);

  const resolveViaAlias = async (kind, suggestionId, alias) => {
    setBusy(true);
    try {
      await api.post(`/admin/masters/${kind}/${suggestionId}/aliases`, { alias });
      toast.success(`Mapped '${alias}'`);
      await api.post(`/admin/uploads/${batchId}/revalidate`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const resolveViaCreate = async (kind, raw) => {
    const draft = addDrafts[`${kind}:${raw}`] || {};
    const payload = { name: raw, aliases: [], ...draft };
    if (kind === "colleges" && !payload.state) {
      toast.error("State is required for colleges");
      return;
    }
    if (kind === "categories") {
      // Scope the new canonical to this upload's counselling body so the
      // parser's resolve_category() picks it up on revalidate.
      payload.counselling_body = data?.batch?.counselling_body;
    }
    setBusy(true);
    try {
      await api.post(`/admin/masters/${kind}`, payload);
      toast.success(`Added '${raw}' to the ${kind} master`);
      await api.post(`/admin/uploads/${batchId}/revalidate`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (counts.total > 0) {
      toast.error("Resolve every unresolved reference first");
      return;
    }
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/admin/uploads/${batchId}/publish`);
      toast.success(`Published ${resp.records} records`);
      nav("/admin/batches");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <p className="p-6 text-center text-sm text-slate-400">Loading…</p>;
  }

  const b = data.batch;
  const items = data.unresolved?.[tab] || [];
  const tabCfg = TABS.find((t) => t.key === tab);

  return (
    <div className="space-y-4" data-testid="admin-review-page">
      <button
        onClick={() => nav("/admin/batches")}
        className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#1A365D]"
      >
        <ArrowLeft className="h-3 w-3" /> Back to batches
      </button>
      <PageHead
        title="Review & Normalize"
        subtitle={`${b.filename} · ${b.counselling_body} · ${b.state} · ${b.year} · ${b.round} · ${b.row_count} rows`}
      />

      <div
        className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border border-slate-200 bg-white px-4 py-3 shadow-sm"
        data-testid="review-summary"
      >
        <span className="text-sm">
          <strong className="font-mono-num text-base" data-testid="review-counter">
            {counts.total}
          </strong>{" "}
          to resolve
        </span>
        <span className="font-mono-num text-xs text-slate-500">
          {counts.colleges} colleges · {counts.courses} courses · {counts.categories} categories · {counts.quotas} quotas
        </span>
        <button
          data-testid="review-publish-btn"
          disabled={counts.total > 0 || busy}
          onClick={publish}
          title={counts.total > 0 ? "Resolve every reference first" : ""}
          className="ml-auto flex items-center gap-1 bg-[#1A365D] px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845] disabled:opacity-40"
        >
          <CheckCircle2 className="h-3 w-3" /> Publish
        </button>
      </div>

      <div className="flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`review-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 whitespace-nowrap border px-3 py-2 text-xs font-semibold uppercase tracking-widest ${
              tab === t.key
                ? "border-[#1A365D] bg-[#1A365D] text-white"
                : "border-slate-300 bg-white text-slate-600"
            }`}
          >
            {t.label}
            <span className="font-mono-num">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <p
          data-testid="review-tab-empty"
          className="border border-emerald-200 bg-emerald-50 px-4 py-6 text-center text-sm text-emerald-800"
        >
          Nothing to review here ✓
        </p>
      ) : (
        <ul className="space-y-2" data-testid="review-items">
          {items.map((it) => (
            <li
              key={it.raw}
              className="border border-slate-200 bg-white p-4"
              data-testid={`review-item-${it.raw}`}
            >
              <p className="font-semibold text-slate-900">{it.raw}</p>
              {it.suggestions.length > 0 && (
                <div className="mt-2 space-y-1" data-testid={`suggestions-${it.raw}`}>
                  {it.suggestions.map((s) => (
                    <div
                      key={s.id}
                      className="flex flex-wrap items-center gap-2 text-sm text-slate-700"
                    >
                      <span className="font-mono-num text-xs text-slate-500">
                        {Math.round(s.score * 100)}%
                      </span>
                      <span className="truncate">{s.name}</span>
                      {s.state && (
                        <span className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                          {s.state}
                        </span>
                      )}
                      <button
                        data-testid={`map-to-${s.id}`}
                        disabled={busy}
                        onClick={() => resolveViaAlias(tab, s.id, it.raw)}
                        className="ml-auto border border-[#1A365D] px-2 py-0.5 text-[11px] font-semibold text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
                      >
                        Map to this
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div
                className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3"
                data-testid={`add-new-${it.raw}`}
              >
                <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  Or add new
                </span>
                {tabCfg.hasState && (
                  <input
                    data-testid={`new-state-${it.raw}`}
                    placeholder="State (required)"
                    value={addDrafts[`${tab}:${it.raw}`]?.state || ""}
                    onChange={(e) =>
                      setAddDrafts((p) => ({
                        ...p,
                        [`${tab}:${it.raw}`]: { ...(p[`${tab}:${it.raw}`] || {}), state: e.target.value },
                      }))
                    }
                    className="h-8 border border-slate-300 px-2 text-xs"
                  />
                )}
                <button
                  data-testid={`add-new-${it.raw}-btn`}
                  disabled={busy}
                  onClick={() => resolveViaCreate(tab, it.raw)}
                  className="flex h-8 items-center gap-1 border border-emerald-600 bg-emerald-50 px-2 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-600 hover:text-white disabled:opacity-40"
                >
                  <Plus className="h-3 w-3" /> Add as new canonical
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
